import type { ThemePreferences } from './themeCatalog';
import { resolveHtmlDataTheme, resolveThemeId } from './themeResolve';

export const THEME_SYNC_MESSAGE_TYPE = 'jarvis:theme-sync';
export const THEME_SYNC_REQUEST_TYPE = 'jarvis:theme-sync-request';

export type ThemeSyncMessage = {
  type: typeof THEME_SYNC_MESSAGE_TYPE;
  preferences: ThemePreferences;
};

export type ThemeSyncRequest = {
  type: typeof THEME_SYNC_REQUEST_TYPE;
};

export function isThemeSyncMessage(data: unknown): data is ThemeSyncMessage {
  if (!data || typeof data !== 'object') return false;
  const record = data as { type?: unknown; preferences?: unknown };
  return record.type === THEME_SYNC_MESSAGE_TYPE && Boolean(record.preferences);
}

export function isThemeSyncRequest(data: unknown): data is ThemeSyncRequest {
  if (!data || typeof data !== 'object') return false;
  return (data as { type?: unknown }).type === THEME_SYNC_REQUEST_TYPE;
}

export function themeSyncMessage(preferences: ThemePreferences): ThemeSyncMessage {
  return { type: THEME_SYNC_MESSAGE_TYPE, preferences };
}

export function htmlModeFromPreferences(preferences: ThemePreferences): 'light' | 'dark' {
  return resolveHtmlDataTheme(preferences, resolveThemeId(preferences));
}

export function postThemeToWindow(
  target: Window | null | undefined,
  preferences: ThemePreferences
): void {
  if (!target || typeof window === 'undefined') return;
  try {
    target.postMessage(themeSyncMessage(preferences), window.location.origin);
  } catch {
    // Cross-origin or detached frames are ignored.
  }
}

/** Notify same-origin workspace/editor iframes so they can apply the new theme live. */
export function broadcastThemeToChildFrames(preferences: ThemePreferences): void {
  if (typeof document === 'undefined') return;
  for (const frame of Array.from(document.querySelectorAll('iframe'))) {
    postThemeToWindow(frame.contentWindow, preferences);
  }
}

/** Nested editor frames ask the parent SPA for the live theme after they boot. */
export function requestThemeFromParent(): void {
  if (typeof window === 'undefined' || window.parent === window) return;
  try {
    window.parent.postMessage({ type: THEME_SYNC_REQUEST_TYPE }, window.location.origin);
  } catch {
    // Top-level or cross-origin parents are ignored.
  }
}
